<template>
  <div class="oa-page">
    <div class="oa-header-card">
      <div class="oa-header-title">流程中心</div>
      <div class="oa-header-sub">欢迎使用 OA 流程管理系统</div>
    </div>

    <div class="oa-stats-row">
      <div class="oa-stat-card todo" @click="goTodo">
        <div class="stat-label">待办任务</div>
        <div class="stat-number">{{ stats.todoCount }}</div>
      </div>
      <div class="oa-stat-card done" @click="goDoneToday">
        <div class="stat-label">已办任务</div>
        <div class="stat-number">{{ stats.completedCount }}</div>
      </div>
      <div class="oa-stat-card start" @click="goStartedToday">
        <div class="stat-label">我的发起</div>
        <div class="stat-number">{{ stats.initiatedCount }}</div>
      </div>
      <div class="oa-stat-card draft" @click="goDrafts">
        <div class="stat-label">抄送到我</div>
        <div class="stat-number">{{ stats.draftCount }}</div>
      </div>
    </div>

    <div class="oa-section">
      <div class="oa-section-header">
        <span class="tag">新增流程</span>
        <span class="title">低代码表单 (LF)</span>
      </div>

      <div v-if="flowsLoading" class="oa-flow-empty">流程加载中...</div>
      <div v-else-if="flowError" class="oa-flow-empty is-error">{{ flowError }}</div>
      <div v-else-if="!flows.length" class="oa-flow-empty">暂无可发起的 LF 流程</div>

      <template v-else>
        <div v-for="(items, category) in groupedFlows" :key="category" class="flow-row">
          <div class="flow-row-label" :style="getCategoryBadgeStyle(category)">{{ category }}</div>
          <div class="flow-row-cards">
            <div v-for="item in items" :key="item.formCode" class="flow-item" @click="openStart(item)">
              <div class="flow-icon">
                <div class="flow-icon-circle" :style="getCategoryIconStyle(item.remark)">
                  <span class="flow-icon-text">{{ item.title ? item.title.substr(0, 1) : '流' }}</span>
                </div>
              </div>
              <div class="flow-info">
                <div class="flow-title" :style="getFlowTitleStyle(item)">{{ item.title }}</div>
                <div class="flow-desc">【{{ hiddenCode(item.formCode) }}】{{ item.description }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="oa-pagination">共 {{ flows.length }} 条记录</div>
      </template>
    </div>
  </div>
</template>

<script>
import { fetchTodoStats, fetchActiveFlows } from '../api/oa';

const DEFAULT_CATEGORY = '其他';
const CATEGORY_THEME_PRESETS = [
  {
    match: (category) => category.indexOf('财务') !== -1,
    theme: { badge: '#fb8c00', icon: '#fb8c00', title: '#fb8c00' }
  },
  {
    match: (category) => category.indexOf('人事') !== -1,
    theme: { badge: '#67c23a', icon: '#67c23a', title: '#67c23a' }
  },
  {
    match: (category) => category.indexOf('信息') !== -1,
    theme: { badge: '#1976d2', icon: '#1976d2', title: '#1976d2' }
  },
  {
    match: (category) => category.indexOf('考勤') !== -1,
    theme: { badge: '#1976d2', icon: '#1976d2', title: '#1976d2' }
  },
];

const CATEGORY_THEME_PALETTE = [
  { badge: '#1976d2', icon: '#1976d2', title: '#1976d2' },
  { badge: '#67c23a', icon: '#67c23a', title: '#67c23a' },
  { badge: '#fb8c00', icon: '#fb8c00', title: '#fb8c00' },
  { badge: '#26a69a', icon: '#26a69a', title: '#26a69a' },
  { badge: '#ef5350', icon: '#ef5350', title: '#ef5350' },
];

export default {
  name: 'OAWorkbench',
  data() {
    return {
      stats: {
        todoCount: 0,
        completedCount: 0,
        initiatedCount: 0,
        draftCount: 0
      },
      flows: [],
      flowsLoading: false,
      flowError: '',
    };
  },
  computed: {
    groupedFlows() {
      const groups = {};
      (this.flows || []).forEach(item => {
        const key = this.normalizeCategory(item.remark);
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(item);
      });
      return groups;
    }
  },
  created() {
    this.loadStats();
    this.loadFlows();
  },
  methods: {
    normalizeCategory(category) {
      const value = String(category || '').trim();
      return value || DEFAULT_CATEGORY;
    },
    getCategoryTheme(category) {
      const normalized = this.normalizeCategory(category);
      if (normalized === DEFAULT_CATEGORY) {
        return { badge: '#9e9e9e', icon: '#9e9e9e', title: '#666666' };
      }

      const preset = CATEGORY_THEME_PRESETS.find(item => item.match(normalized));
      if (preset) {
        return preset.theme;
      }

      const seed = Array.from(normalized).reduce((sum, char) => sum + char.charCodeAt(0), 0);
      return CATEGORY_THEME_PALETTE[seed % CATEGORY_THEME_PALETTE.length];
    },
    getCategoryBadgeStyle(category) {
      return {
        background: this.getCategoryTheme(category).badge,
      };
    },
    getCategoryIconStyle(category) {
      return {
        background: this.getCategoryTheme(category).icon,
      };
    },
    getFlowTitleStyle(item) {
      return {
        color: this.getCategoryTheme(item && item.remark).title,
      };
    },
    async loadStats() {
      try {
        const res = await fetchTodoStats();
        if (res && res.code === 200 && res.data) {
          const d = res.data;
          this.stats = {
            todoCount: d.todoCount || 0,
            completedCount: d.doneTodayCount || 0,
            initiatedCount: d.doneCreateCount || 0,
            draftCount: d.draftCount || d.copyToMeCount || 0
          };
        }
      } catch (e) {
        console.error('获取 OA 统计失败', e);
      }
    },
    async loadFlows() {
      this.flowsLoading = true;
      this.flowError = '';
      try {
        const res = await fetchActiveFlows();
        if (res && res.code === 200 && Array.isArray(res.data)) {
          this.flows = res.data.map(c => ({
            formCode: c.key,
            title: c.value,
            formType: c.type,
            hasChooseApprove: c.hasStarUserChooseModule,
            description: c.description || `${c.value}流程办理`,
            remark: this.normalizeCategory(c.remark)
          }));
          return;
        }

        this.flows = [];
        this.flowError = (res && res.msg) || '获取 LF 流程列表失败';
      } catch (e) {
        console.error('获取 OA 流程列表失败', e);
        this.flows = [];
        this.flowError = '获取 LF 流程列表失败';
      } finally {
        this.flowsLoading = false;
      }
    },
    hiddenCode(code) {
      if (!code) return '';
      if (code.length <= 8) return code;
      return code.substr(0, 4) + '****' + code.substr(code.length - 4);
    },
    openStart(item) {
      if (!item || !item.formCode) {
        this.$message.warning('未获取到流程编码，无法发起');
        return;
      }

      const params = {
        formType: item.formType,
        formCode: item.formCode,
        hasChooseApprove: item.hasChooseApprove,
        title: item.title,
        description: item.description
      };

      this.$router.push({
        path: '/home/oa/start',
        query: {
          params: encodeURIComponent(JSON.stringify(params))
        }
      });
    },
    openTaskList(mode) {
      this.$router.push({
        path: '/home/oa/tasks',
        query: { mode }
      });
    },
    goTodo() {
      this.openTaskList('todo');
    },
    goDoneToday() {
      this.openTaskList('done');
    },
    goStartedToday() {
      this.openTaskList('started');
    },
    goDrafts() {
      this.openTaskList('copy');
    }
  }
};
</script>

<style scoped>
.oa-page {
  height: 100%;
  padding: 16px 24px;
  background: #f5f5f7;
  box-sizing: border-box;
  overflow-y: auto;
}

.oa-header-card {
  background: linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%);
  border-radius: 12px;
  padding: 16px 20px;
  border: 1px solid #d6e7ff;
  margin-bottom: 16px;
}

.oa-header-title {
  font-size: 18px;
  font-weight: 600;
  color: #0d7deb;
}

.oa-header-sub {
  margin-top: 4px;
  font-size: 13px;
  color: #6b7c93;
}

.oa-stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.oa-stat-card {
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.oa-stat-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
}

.oa-stat-card .stat-label {
  font-size: 13px;
}

.oa-stat-card .stat-number {
  margin-top: 6px;
  font-size: 20px;
  font-weight: 600;
}

.oa-stat-card.todo { background: #1976d2; }
.oa-stat-card.done { background: #67c23a; }
.oa-stat-card.start { background: #fb8c00; }
.oa-stat-card.draft { background: #ef5350; }

.oa-section {
  margin-top: 18px;
  padding: 16px 18px 18px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.oa-section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.oa-section-header .tag {
  background: #0d7deb;
  color: #fff;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  margin-right: 8px;
}

.oa-section-header .title {
  font-size: 16px;
  font-weight: 600;
}

.oa-flow-empty {
  padding: 28px 16px;
  border-radius: 12px;
  text-align: center;
  font-size: 13px;
  color: #909399;
  background: #fafafa;
}

.oa-flow-empty.is-error {
  color: #ef5350;
}

.flow-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
}

.flow-row:last-of-type {
  margin-bottom: 0;
}

.flow-row-label {
  min-width: 60px;
  text-align: center;
  border-radius: 10px;
  padding: 6px 8px;
  font-size: 12px;
  line-height: 1.2;
  color: #fff;
  margin-right: 10px;
  box-sizing: border-box;
}

.flow-row-cards {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
}

.flow-item {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 10px 8px;
  border: 1px solid #d0d0d0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.flow-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
}

.flow-icon {
  margin-bottom: 8px;
}

.flow-icon-circle {
  width: 32px;
  height: 32px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.flow-icon-text {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.flow-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.flow-desc {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.oa-pagination {
  margin-top: 14px;
  text-align: center;
  font-size: 12px;
  color: #999;
}
</style>
